import type { z } from "zod";

/** Storage can be absent (SSR, a test without jsdom) or present but unusable (Safari private mode throws). */
function getStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * One schema-validated client-side UI preference backed by a single `localStorage`
 * key (adr/0007) — table column choices, filter presets, and similar
 * non-authoritative state. Persistence is per-browser, not per-account.
 *
 * `key` is used verbatim, so the CALLER namespaces it per surface — e.g.
 * `"data-table:users:columns"` — keeping preferences independently resettable.
 *
 * @example
 * const preference = new StoredPreference("data-table:users:columns", ColumnPreferenceSchema);
 * const { columnVisibility } = preference.read(defaults);
 */
class StoredPreference<TValue> {
  private readonly key: string;
  private readonly schema: z.ZodType<TValue>;

  constructor(key: string, schema: z.ZodType<TValue>) {
    this.key = key;
    this.schema = schema;
  }

  /**
   * The stored value, or `fallback` when there is nothing valid to read. Pure: a
   * corrupt or stale-shaped entry degrades to `fallback` and is left in storage, so
   * nothing mutates as a side effect of rendering.
   */
  read(fallback: TValue): TValue {
    const raw = getStorage()?.getItem(this.key);
    if (raw === null || raw === undefined) return fallback;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return fallback;
    }

    const result = this.schema.safeParse(parsed);
    return result.success ? result.data : fallback;
  }

  /** Persist a value. Storage failures are swallowed — a preference is never worth throwing over. */
  write(value: TValue): void {
    try {
      getStorage()?.setItem(this.key, JSON.stringify(value));
    } catch {
      // Quota exceeded, or storage blocked: the UI keeps working with its in-memory state.
    }
  }

  /** Remove the entry, so the next read returns the caller's fallback. */
  clear(): void {
    try {
      getStorage()?.removeItem(this.key);
    } catch {
      // Same reasoning as write.
    }
  }
}

export { StoredPreference };
